// services/AssignSlatesService.js
import { api } from "../../services/NetworkBase";

/**
 * Search for slates by query string
 * Matches VB.NET SelectJobSlates functionality
 * @param {string} query - Search query string
 * @returns {Promise<Object>} - { results: SlateSearchResultDto[] }
 */
export async function SearchSlates(query) {
  try {
    const response = await api
      .get(`vlp/assign-slates/search?query=${encodeURIComponent(query)}`)
      .json();
    return response;
  } catch (error) {
    console.error("Error in SearchSlates:", error);
    throw new Error(error.message || "Failed to search slates");
  }
}

/**
 * Get tests for a specific slate
 * Matches VB.NET ShowAssignedTests functionality
 * @param {string} jobId - Slate job ID
 * @returns {Promise<Object>} - { results: SlateTestDto[] }
 */
export async function GetSlateTests(jobId) {
  try {
    if (!jobId) {
      throw new Error("Job ID is required");
    }

    const response = await api
      .get(`vlp/assign-slates/slate-tests/${encodeURIComponent(jobId)}`)
      .json();
    return response;
  } catch (error) {
    console.error("Error in GetSlateTests:", error);
    throw new Error(error.message || "Failed to get slate tests");
  }
}

/**
 * Assign tests to a sample
 * Matches VB.NET AssignTests functionality
 * NOTE: VB.NET just validates and returns test codes - parent handles DB insert
 * @param {string} sampleNumber - Sample number
 * @param {string} jobId - Slate job ID
 * @param {string[]} testCodes - Array of test codes to assign
 * @returns {Promise<Object>} - { success, message, assignedTests }
 */
export async function AssignTestsToSample(sampleNumber, jobId, testCodes) {
  try {
    if (!sampleNumber) {
      throw new Error("Sample number is required");
    }

    if (!testCodes || testCodes.length === 0) {
      throw new Error("No tests selected");
    }

    const response = await api
      .post("vlp/assign-slates/assign-to-sample", {
        json: {
          sampleNumber,
          jobId,
          testCodes,
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in AssignTestsToSample:", error);
    throw new Error(error.message || "Failed to assign tests to sample");
  }
}

/**
 * Assign slates to a quote
 * Matches VB.NET AssignSlatestoQuote functionality
 * Creates quote transaction records with account-specific pricing
 * @param {string} quoteNumber - Quote number
 * @param {string} accountNumber - Account number for pricing
 * @param {string[]} jobIds - Array of slate job IDs to assign
 * @returns {Promise<Object>} - { success, message, transactions }
 */
export async function AssignSlatesToQuote(quoteNumber, accountNumber, jobIds) {
  try {
    if (!quoteNumber) {
      throw new Error("Quote number is required");
    }

    if (!jobIds || jobIds.length === 0) {
      throw new Error("No slates selected");
    }

    const response = await api
      .post("vlp/assign-slates/assign-to-quote", {
        json: {
          quoteNumber,
          accountNumber,
          jobIds,
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in AssignSlatesToQuote:", error);
    throw new Error(error.message || "Failed to assign slates to quote");
  }
}

/**
 * Assign tests to a slate
 * Matches VB.NET AssignTeststoSlate functionality
 * Adds tests to slate's jobtests with automatic sort order
 * @param {string} jobId - Slate job ID
 * @param {string[]} testCodes - Array of test codes to assign
 * @returns {Promise<Object>} - { success, message, assignedCount }
 */
export async function AssignTestsToSlate(jobId, testCodes) {
  try {
    if (!jobId) {
      throw new Error("Job ID is required");
    }

    if (!testCodes || testCodes.length === 0) {
      throw new Error("No tests selected");
    }

    const response = await api
      .post("vlp/assign-slates/assign-to-slate", {
        json: {
          jobId,
          testCodes,
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in AssignTestsToSlate:", error);
    throw new Error(error.message || "Failed to assign tests to slate");
  }
}

/**
 * Assign slate to bottle order
 * Matches VB.NET AssignTeststoBottleOrder functionality
 * Transfers slate info (COC, bottle, preservation) to bottle order
 * @param {number} orderNumber - Bottle order number
 * @param {string} jobId - Slate job ID
 * @returns {Promise<Object>} - { success, message, orderedBottle }
 */
export async function AssignSlateToBottleOrder(orderNumber, jobId) {
  try {
    if (!orderNumber || orderNumber <= 0) {
      throw new Error("Valid order number is required");
    }

    if (!jobId) {
      throw new Error("Job ID is required");
    }

    const response = await api
      .post("vlp/assign-slates/assign-to-bottle-order", {
        json: {
          orderNumber,
          jobId,
        },
      })
      .json();
    return response;
  } catch (error) {
    console.error("Error in AssignSlateToBottleOrder:", error);
    throw new Error(error.message || "Failed to assign slate to bottle order");
  }
}

/**
 * Get slate cost for specific account
 * Matches VB.NET vJobCostqry functionality
 * Returns account-specific pricing or default slate pricing
 * @param {string} jobId - Slate job ID
 * @param {string} accountNumber - Account number (optional)
 * @returns {Promise<Object>} - SlateCostDto
 */
export async function GetSlateCost(jobId, accountNumber) {
  try {
    if (!jobId) {
      throw new Error("Job ID is required");
    }

    const url = accountNumber
      ? `vlp/assign-slates/slate-cost/${encodeURIComponent(jobId)}?accountNumber=${encodeURIComponent(accountNumber)}`
      : `vlp/assign-slates/slate-cost/${encodeURIComponent(jobId)}`;

    const response = await api.get(url).json();
    return response;
  } catch (error) {
    console.error("Error in GetSlateCost:", error);
    throw new Error(error.message || "Failed to get slate cost");
  }
}